/**
 * The small Markdown subset articles are written in, parsed to plain data so
 * the player site and the console render it as elements (never as HTML, so
 * nothing typed by an admin or written by the AI can inject markup):
 *
 *   ## Heading / ### Subheading
 *   - bullet / 1. numbered
 *   > quote
 *   paragraphs separated by a blank line, with **bold** and *italic* inside
 */

export interface Span {
  text: string;
  bold?: boolean;
  italic?: boolean;
}

export type Block =
  | { type: 'h2' | 'h3' | 'p' | 'quote'; spans: Span[] }
  | { type: 'ul' | 'ol'; items: Span[][] };

/** **bold** and *italic* (or _italic_). Unclosed markers stay as text. */
export function parseInline(s: string): Span[] {
  const out: Span[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*\s][^*]*)\*|_([^_\s][^_]*)_/g;
  let last = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push({ text: s.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ text: m[1], bold: true });
    else out.push({ text: (m[2] ?? m[3])!, italic: true });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last) });
  return out.filter((x) => x.text);
}

export function parseMarkdown(src: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { type: 'ul' | 'ol'; items: Span[][] } | null = null;
  const flush = () => {
    if (para.length) blocks.push({ type: 'p', spans: parseInline(para.join(' ')) });
    para = [];
    if (list) blocks.push(list);
    list = null;
  };
  for (const raw of src.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    const bullet = /^[-*•]\s+(.+)$/.exec(line);
    const numbered = /^\d{1,2}[.)]\s+(.+)$/.exec(line);
    const quote = /^>\s?(.*)$/.exec(line);
    if (heading) {
      flush();
      blocks.push({ type: heading[1]!.length === 3 ? 'h3' : 'h2', spans: parseInline(heading[2]!.replace(/#+$/, '').trim()) });
    } else if (bullet || numbered) {
      const type = bullet ? 'ul' : 'ol';
      if (para.length || (list && list.type !== type)) flush();
      list ??= { type, items: [] };
      list.items.push(parseInline((bullet ?? numbered)![1]!));
    } else if (quote) {
      flush();
      blocks.push({ type: 'quote', spans: parseInline(quote[1]!) });
    } else {
      if (list) flush();
      para.push(line);
    }
  }
  flush();
  return blocks;
}

/** Plain text of the first paragraphs, for cards and meta descriptions. */
export function plainText(src: string, max = 200): string {
  const text = parseMarkdown(src)
    .flatMap((b) => ('spans' in b ? [b.spans] : b.items))
    .map((spans) => spans.map((s) => s.text).join(''))
    .join(' ');
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}
